import Vue from 'vue';
import Moment from 'moment-timezone';
import * as toastr from 'toastr';
import { uniq } from 'lodash';
import { i18n } from '@/plugins/i18n';
import { doAsync, setAsyncValue } from '@/utils/async';
import { SET_COMPANY, GET_CONFIG_ACTION_PLANS } from '@/store/modules/config/mutation-types';
import { SET_CREDENTIALS } from '@/store/modules/socket/mutation-types';
import { LEAVE_MEETING } from '@/store/modules/meeting/action-types';
// Socket handling
import { SEND_MESSAGE } from '@/store/modules/socket/action-types';
import SocketMessages from '@/utils/classes/SocketMessages';
// Other utils
import { calert, handleUserEditionError } from '@/utils/main';
import ErrorCode from '@/utils/classes/ErrorCode';
import { DELAY_LOGIN_ATTEMPT } from '@/utils/constants';
import { NotificationType } from '@/utils/notification-utils';
import IAAnalyser from '@/utils/classes/IAAnalyser';
import * as mutationsTypes from './mutation-types';
import * as actionsTypes from './action-types';

/**
 * Handle the XHR response of indicators/get.php as an array of themes
 * @param  {Object} response  XHR response with a 'data' prop (which is an array of themes)
 * @param  {Object} payload   XHR post params
 * @return {Array}            Array of themes
 */
function handleIndicatorsResponse(store, response, payload) {
  if (response && response.data instanceof Array) {
    const range = payload ? payload.range : undefined;
    // Analyse if required
    const { themes, msg } = payload && payload.requireAnalysis
      ? IAAnalyser.analyseIndicators(response.data, Moment(payload.range.end))
      : { themes: response.data, msg: null };
    themes.forEach((theme) => {
      (theme.indicators || []).forEach((indicator) => {
        indicator.id = Number(indicator.id);
        store.commit(mutationsTypes.SET_INDICATOR, { indicator, range });
      });
    });

    // Save the analysis message
    if (payload && payload.requireAnalysis && msg !== null) {
      store.commit(mutationsTypes.SET_INDICATOR_ANALYSIS_MSG, msg);
    }
    return themes;
  }
  return null;
}

/**
 * CRUD Handler for Problem types actions
 */
async function pbTypeAction(store, payload, method) {
  let url = '/server/problem/types';
  switch (method) {
    case 'put':
      url += `/${payload.id}`;
      break;
    case 'delete':
      url += `/${payload.id}/${payload.deleteChildren}`;
      payload = {};
      break;
    default:
      break;
  }
  return doAsync(store, {
    config: { method, url, data: payload },
    mutationTypes: mutationsTypes.GET_PB_TYPES,
    callback: response => (response.data instanceof Array ? response.data : null),
  })
    .then((response) => {
      if (!(response.data instanceof Array)) {
        toastr.error(i18n.t('message.CONNECTION_ERROR'));
        throw response;
      }
      return response;
    })
    .catch((err) => {
      if (!store.state.device.offlineMode) {
        toastr.error(i18n.t('message.CONNECTION_ERROR'));
      }
      throw err;
    });
}

/**
 * CRUD Handler for Zone actions
 */
async function zoneAction(store, payload, method) {
  let url = '/server/locations';
  switch (method) {
    case 'put':
      url += `/${payload.id}`;
      break;
    case 'delete':
      url += `/${payload.id}/${payload.deleteChildren}`;
      payload = {};
      break;
    default:
      break;
  }

  return doAsync(store, {
    config: { method, url, data: payload },
    mutationTypes: mutationsTypes.GET_ZONES,
    callback: response => (response.data instanceof Array ? response.data : null),
  })
    .then((response) => {
      if (!(response.data instanceof Array)) {
        toastr.error(i18n.t('message.CONNECTION_ERROR'));
        throw response;
      }
      return response;
    })
    .catch((err) => {
      if (!store.state.device.offlineMode) {
        toastr.error(i18n.t('message.CONNECTION_ERROR'));
      }
      throw err;
    });
}


// Indicators requests grouping
// On a cfg page, each indicator widget ask for an indicators request
// Group requests when the range is the same in order to run a single request with multiple indicator IDs
let pendingMgtIndicatorsRequests = [];
let pendingIndicatorsRequests = [];
let pendingMgtIndicatorsRequestTimeout = null;
let pendingIndicatorsRequestTimeout = null;


export default {

  /** Log the user in by setting up PHP variables and cookies */
  async [actionsTypes.LOGIN](store, payload) {
    store.commit(mutationsTypes.SET_ATTEMPTING_LOGIN, true);
    return new Promise((resolve, reject) => {
      Vue.axios({
        method: 'post',
        url: '/server/auth/login',
        data: payload,
      })
        .then((response) => {
          if (!response) return;
          const { data } = response;
          if (typeof data === 'object') {
            store.commit(mutationsTypes.SET_ATTEMPTING_LOGIN, false);
            if (payload.user) {
              store.commit(mutationsTypes.SET_CURRENT_JWT, data.jwt);
              store.commit(mutationsTypes.SET_CURRENT_USER, data);
              store.commit(mutationsTypes.SET_CURRENT_TEAM, { ...data, id: data.teamId });
              if (data.companyDetails) {
                store.commit(`config/${SET_COMPANY}`, data.companyDetails);
              }
            } else {
              store.commit(mutationsTypes.SET_CURRENT_JWT, data.jwt);
              store.commit(mutationsTypes.SET_CURRENT_TEAM, data);
            }
          } else {
            // Delay the next login attempt in case of failure
            setTimeout(() => {
              store.commit(mutationsTypes.SET_ATTEMPTING_LOGIN, false);
              toastr.error(i18n.t('message.CONNECTION_ERROR'));
            }, DELAY_LOGIN_ATTEMPT);
          }
        })
        .catch((error) => {
          let toastrMessage;
          if (!error.response || !error.response.data) {
            toastrMessage = i18n.t('message.CONNECTION_ERROR');
          } else {
            const { response: { data } } = error;
            if (data && data instanceof Object && data.code !== undefined) {
              if (data.code === ErrorCode.LOGIN_WRONG_ID || data.code === ErrorCode.LOGIN_WRONG_PWD) {
                // Wrong credentials message
                toastrMessage = i18n.t('message.WRONG_CREDENTIALS');
              } else if (data.code === ErrorCode.LOGIN_SSO_NOT_ENABLED) {
                // User tried to connect via SSO but SSO isn't configured for the company
                toastrMessage = i18n.t('message.LOGIN_SSO_NOT_ENABLED');
              } else if (data.code === ErrorCode.LOGIN_LAST_ATTEMPT_TOO_CLOSE) {
                // User tried to connect less than 2 seconds after a failes attempt (brute forcing lock)
                toastrMessage = i18n.t('message.LOGIN_LAST_ATTEMPT_TOO_CLOSE');
              } else if (data.code === ErrorCode.LOGIN_SSO_SAML_REQUIRED) {
                // SSO SAML required => No message, redirect to SAML
                window.location = '/server/saml/sso';
              } else if (data.code === ErrorCode.LOGIN_SSO_OIDC_REQUIRED) {
                // SSO OIDC required => No message, redirect to OIDC
                window.location = '/server/oidc/sso';
              } else {
                toastrMessage = i18n.t('message.CONNECTION_ERROR');
              }
            } else {
              toastrMessage = i18n.t('message.CONNECTION_ERROR');
            }
          }

          // Delay the next login attempt in case of failure
          setTimeout(() => {
            store.commit(mutationsTypes.SET_ATTEMPTING_LOGIN, false);
            if (toastrMessage) {
              toastr.error(toastrMessage);
            }
            reject(error);
          }, DELAY_LOGIN_ATTEMPT);
        });
    });
  },

  /** Log the user out */
  async [actionsTypes.LOGOUT](store, payload) {
    if (store.state.meeting.isMeetingInProgress) {
      calert(i18n.t('message.NOTICE_LEAVE_MEETING'), [
        { title: i18n.t('message.RESUME') },
        {
          title: i18n.t('message.LOGOUT'),
          action: () => {
            store.dispatch(`meeting/${LEAVE_MEETING}`, {
              callback: () => { store.dispatch(actionsTypes.LOGOUT, payload); },
            });
          },
        },
      ]);
      return 0;
    }
    toastr.info(i18n.t('message.LOGGING_OUT'));
    return Vue.axios({
      method: 'post',
      url: '/server/auth/logout',
    })
      .then((response) => {
        if (!response) throw response;
        if (response.data === 1) {
          // Reset the state
          store.commit('resetState');
          store.commit(`config/${SET_COMPANY}`, null);
          store.commit(mutationsTypes.SET_CURRENT_TEAM, null);
          store.commit(mutationsTypes.SET_CURRENT_USER, null);
          store.commit(`socket/${SET_CREDENTIALS}`, null);
          if (payload.app) {
            payload.app.$router.push('/login');
          }
          return response.data;
        }
        toastr.eror(i18n.t('message.CONNECTION_ERROR'));
        throw response.data;
      })
      .catch((error) => {
        if (!error.response || !error.response.data) {
          toastr.error(i18n.t('message.CONNECTION_ERROR'));
          return;
        }
        const { response: { data } } = error;

        if (data && data instanceof Object && data.code !== undefined) {
          if (data.code === ErrorCode.LOGIN_SSO_SAML_REQUIRED) {
            // SSO SAML required => Redirect to saml/slo
            window.location = '/server/saml/slo';
          } else if (data.code === ErrorCode.LOGIN_SSO_OIDC_REQUIRED) {
            // SSO OIDC required => Redirect to oidc/slo
            window.location = '/server/oidc/slo';
          }
        } else {
          toastr.error(i18n.t('message.CONNECTION_ERROR'));
        }
      });
  },

  /** Check PHP vars and cookies to automatically log the user */
  async [actionsTypes.CHECK_LOGIN](store) {
    return Vue.axios({
      method: 'post',
      url: '/server/auth/checkLogin',
      data: {},
    })
      .then((response) => {
        if (!response || response.data === 0) {
          throw response;
        }
        const { data } = response;
        if (typeof data === 'object') {
          if (data.isUser && data.username !== undefined) {
            store.commit(mutationsTypes.SET_CURRENT_JWT, data.jwt);
            store.commit(mutationsTypes.SET_CURRENT_USER, data);
            store.commit(mutationsTypes.SET_CURRENT_TEAM, { ...data, id: data.teamId });
            if (data.companyDetails) {
              store.commit(`config/${SET_COMPANY}`, data.companyDetails);
            }
          } else {
            store.commit(mutationsTypes.SET_CURRENT_JWT, data.jwt);
            store.commit(mutationsTypes.SET_CURRENT_TEAM, data);
          }
          return data;
        }
        throw response;
      })
      .catch((error) => {
        if (!error.response || !error.response.data) {
          throw error;
        }
        const { response: { data } } = error;
        if (data && data instanceof Object && data.code !== undefined) {
          if (data.code === ErrorCode.LOGIN_SSO_SAML_REQUIRED) {
            // SSO SAML required => No message, redirect to SAML
            window.location = '/server/saml/sso';
            return 0;
          }
          if (data.code === ErrorCode.LOGIN_SSO_OIDC_REQUIRED) {
            // SSO OIDC required => No message, redirect to OIDC
            window.location = '/server/oidc/sso';
            return 0;
          }
        } else if (error.response.status === 401 && error.response.data.jwt) {
          store.commit(mutationsTypes.SET_CURRENT_JWT, error.response.data.jwt);
          return store.dispatch(actionsTypes.CHECK_LOGIN);
        }
        throw error;
      });
  },

  async [actionsTypes.FETCH_PENDING_ACTIONS_NUMBER](store, useSocket) {
    if (!store.getters.needPendingActionsNumber && store.state.setPendingActionsNumberPending) {
      return {};
    }
    return doAsync(store, {
      config: {
        method: 'post',
        url: '/server/actions/getPendingActionsNumber.php',
      },
      mutationTypes: mutationsTypes.GET_PENDING_ACTIONS_NUMBER,
      callback: ({ data }) => (data && data !== -1 ? data : 0),
    })
      .then(() => {
        if (useSocket) {
          store.dispatch(`socket/${SEND_MESSAGE}`, SocketMessages.CC_MSGS.PENDING_ACTIONS_NUMBER);
        }
      });
  },

  /** Change the current team of a user so he can see the content of another team */
  async [actionsTypes.SWITCH_USER_TEAM](store, payload) {
    // TODO: Handle user connected to 2 devices
    if (store.state.meeting.isMeetingInProgress) {
      calert(i18n.t('message.NOTICE_LEAVE_MEETING'), [
        { title: i18n.t('message.RESUME') },
        {
          title: i18n.t('message.LOGOUT'),
          action: () => {
            store.dispatch(`meeting/${LEAVE_MEETING}`, {
              callback: () => { store.dispatch(actionsTypes.SWITCH_USER_TEAM, payload); },
            });
          },
        },
      ]);
      return 0;
    }
    return Vue.axios({
      method: 'post',
      url: '/server/users/switchTeam.php',
      data: { team: payload.team },
    })
      .then((response) => {
        if (!response || response.data === 0) {
          throw response;
        }
        const { data } = response;
        if (typeof data === 'object' && data.isUser && data.username !== undefined) {
          // Reset the state
          store.commit('resetState');
          // Set the new user in the new state
          store.commit(mutationsTypes.SET_CURRENT_USER, data);
          store.commit(mutationsTypes.SET_CURRENT_TEAM, { ...data, id: data.teamId });
          if (!payload.noSocketMessage) {
            store.dispatch(`socket/${SEND_MESSAGE}`, {
              msg: SocketMessages.USER_SWITCHED_TEAM,
              socketUser: 1,
              teamId: data.teamId,
            });
          }
          return data;
        }
        toastr.error(i18n.t('message.CONNECTION_ERROR'));
        throw response;
      });
  },

  /** Change a team parameter */
  async [actionsTypes.SET_TEAM_PARAM](store, payload) {
    return Vue.axios({
      method: 'put',
      url: '/server/team/parameters',
      data: payload,
    })
      .then((response) => {
        if (response && typeof response.data === 'object') {
          store.commit(mutationsTypes.SET_CURRENT_TEAM_PARAM, { payload, response });
          toastr.success(i18n.t('message.SETTINGS_SAVED'));
        }
        return response.data;
      })
      .catch((error) => {
        if (!error.response || !error.response.data) {
          toastr.error(i18n.t('message.CONNECTION_ERROR'));
          return;
        }
        const { response: { data } } = error;

        if (data && data instanceof Object && data.code !== undefined) {
          if (data.code === ErrorCode.TEAM_NOT_FOUND) {
            // Bad team
            toastr.error(i18n.t('message.TEAM_NOT_FOUND'));
          } else if (data.code === ErrorCode.CANNOT_SAVE_TEAM_PARAMETERS) {
            // can not save parameters
            toastr.error(i18n.t('message.CANNOT_SAVE_TEAM_PARAMETERS'));
          }
        } else if (error.response.status === 401) {
          toastr.error(i18n.t('message.ERROR_UNAUTHORIZE'));
        } else {
          toastr.error(i18n.t('message.CONNECTION_ERROR'));
        }
      });
  },

  async [actionsTypes.FETCH_TEAMS](store, payload) {
    return doAsync(store, {
      config: {
        method: 'post',
        url: '/server/teams/get.php',
        data: { users: true, mgt: true, ...payload },
      },
      mutationTypes: mutationsTypes.GET_TEAMS,
      callback: response => (response.data instanceof Array ? response.data : null)
        .map((team) => {
          const teamObj = {
            ...team,
            title: team.name,
            users: team.users.map(user => ({
              ...user,
              label: i18n.t('fullNameFormat', user),
              // radioId is used in problem sheet edition
              radioId: `${team.id}-${user.id}`,
            })),
          };
          return teamObj;
        }),
    })
      .then((response) => {
        if (response.data instanceof Array) {
          if (store.state.currentTeam) {
            response.data.forEach((team) => {
              if (team.id === store.state.currentTeam.id && team.users instanceof Array) {
                store.commit(mutationsTypes.SET_CURRENT_TEAM, { ...store.state.currentTeam, users: team.users });
              }
            });
          }
          return response;
        }
        throw response;
      });
  },

  /** Get current team's users */
  async [actionsTypes.FETCH_USERS](store) {
    const teamId = store.state.currentTeam.id || 0;
    return doAsync(store, {
      config: {
        method: 'get',
        url: `/server/teams/${teamId}/users`,
      },
      mutationTypes: mutationsTypes.GET_USERS,
      callback: (response) => {
        if (response.data instanceof Array) {
          return response.data.map(user => ({
            ...user,
            label: `${user.firstName} ${user.lastName}`,
          }));
        }
        return null;
      },
    })
      .then(({ data }) => {
        if (!(data instanceof Array)) {
          toastr.error(i18n.t('message.CONNECTION_ERROR'));
        }
      });
  },

  /** Get a specific user's data */
  async [actionsTypes.FETCH_USER_DATA](store, payload) {
    store.commit(mutationsTypes.SET_USER_DATA_LODAING, { ...payload, loading: true });
    return Vue.axios({
      method: 'get',
      url: `/server/users/${payload.id}`,
    })
      .then((response) => {
        if (response && typeof response.data === 'object') {
          const { data } = response;
          store.commit(mutationsTypes.SET_USER_DATA, { ...data, label: i18n.t('fullNameFormat', data) });
          store.commit(mutationsTypes.SET_USER_DATA_LODAING, { ...payload, loading: false });
        }
      })
      .catch((error) => {
        if ((!error.response || !error.response.data) && (!store.state.device.offlineMode)) {
          toastr.error(i18n.t('message.CONNECTION_ERROR'));
          return;
        }
        const { response } = error;
        if (response) {
          if (response.data && response.data instanceof Object && response.data.code !== undefined) {
            if (response.data.code === ErrorCode.USER_NOT_FOUND) {
              toastr.error(i18n.t('message.USER_NOT_FOUND'));
            }
          } else {
            toastr.error(i18n.t('message.CONNECTION_ERROR'));
          }
        }
      });
  },

  /** Get current team's users */
  async [actionsTypes.EDIT_USER](store, payload) {
    return Vue.axios({
      method: 'put',
      url: `/server/users/${payload.id}`,
      data: { ...payload, locale: i18n.t('min') },
    })
      .then((response) => {
        if (!response) throw response;
        if (response.data instanceof Array) {
          toastr.success(i18n.t('message.USER_UPDATED'));
          const users = response.data.map(user => ({ ...user, label: i18n.t('fullNameFormat', user) }));
          setAsyncValue(store, mutationsTypes.GET_USERS, users);
          store.commit(mutationsTypes.SET_CURRENT_USER_PARAM, { users });
          return users;
        }
        if (response.data instanceof Object) {
          handleUserEditionError(response);
        } else {
          toastr.error(i18n.t('message.CONNECTION_ERROR'));
        }
        throw response;
      })
      .catch((error) => {
        if (!error.response || !error.response.data) {
          toastr.error(i18n.t('message.CONNECTION_ERROR'));
        } else {
          const { response: { data } } = error;
          if (data && data instanceof Object && data.code !== undefined) {
            if (data.code === ErrorCode.EMAIL_ALREADY_USE) {
              toastr.error(i18n.t('message.CONFIG_EMAIL_ALREADY_IN_USE'));
            } else if (data.code === ErrorCode.NOTHING_TO_UPDATE) {
              toastr.error(i18n.t('message.NOTHING_TO_UPDATE'));
            } else if (data.code === ErrorCode.VALIDATOR_ERROR && data.error.username) {
              toastr.error(i18n.t('message.CONFIG_USERNAME_ALREADY_EXISTS'));
            }
          } else {
            toastr.error(i18n.t('message.CONNECTION_ERROR'));
          }
        }
      });
  },

  /** Fetch a user picture */
  async [actionsTypes.FETCH_USER_PICTURE](store, payload) {
    store.commit(mutationsTypes.SET_USER_PICTURE_LOADING, { ...payload, loading: true });
    Vue.axios({
      method: 'get',
      url: '/server/users/getPicture.php',
      params: payload,
    })
      .then((response) => {
        const { data } = response;
        store.commit(mutationsTypes.SET_USER_PICTURE_LOADING, { ...payload, loading: false });
        if (data instanceof Object) {
          store.commit(mutationsTypes.SET_USER_PICTURE, { ...payload, data: data.data });
          return data;
        }
        store.commit(mutationsTypes.SET_USER_PICTURE, { ...payload, data: '' });
        throw response;
      });
  },

  /** Edit a user picture */
  async [actionsTypes.EDIT_USER_PICTURE](store, payload) {
    store.commit(mutationsTypes.SET_USER_PICTURE_LOADING, { ...payload, loading: true });
    Vue.axios({
      method: 'post',
      url: '/server/users/setPicture.php',
      data: payload,
    })
      .then((response) => {
        const { data } = response;
        if (data instanceof Object && data.data !== undefined) {
          toastr.success(i18n.t('message.PICTURE_SAVED'));
          store.commit(mutationsTypes.SET_USER_PICTURE, { id: payload.userId, data: data.data });
          return data;
        }
        toastr.error(i18n.t('message.CONNECTION_ERROR'));
        throw response;
      });
  },

  /** Delete a user picture */
  async [actionsTypes.DELETE_USER_PICTURE](store, payload) {
    store.commit(mutationsTypes.SET_USER_PICTURE_LOADING, { ...payload, loading: true });
    Vue.axios({
      method: 'post',
      url: '/server/users/deletePicture.php',
      data: payload,
    })
      .then((response) => {
        const { data } = response;
        if (data === 1) {
          toastr.success(i18n.t('message.PICTURE_SAVED'));
          store.commit(mutationsTypes.SET_USER_PICTURE, { id: payload.userId, data: null });
          return 1;
        }
        toastr.error(i18n.t('message.CONNECTION_ERROR'));
        throw response;
      });
  },

  async [actionsTypes.FETCH_ROLES](store, payload) {
    return doAsync(store, {
      config: {
        method: 'post',
        url: '/server/meetings/getRoles.php',
        data: payload,
      },
      mutationTypes: mutationsTypes.GET_ROLES,
      callback: response => (response.data instanceof Array ? response.data : []),
    })
      .then(({ data }) => {
        if (!(data instanceof Array)) {
          toastr.error(i18n.t('message.CONNECTION_ERROR'));
        }
      });
  },

  async [actionsTypes.FETCH_ACTION_PLANS](store, payload) {
    return doAsync(store, {
      config: {
        method: 'post',
        url: '/server/actionPlans/get.php',
        data: { ...payload, actions: true },
      },
      mutationTypes: payload && payload.config ? GET_CONFIG_ACTION_PLANS : mutationsTypes.GET_ACTION_PLANS,
      callback: response => (response.data instanceof Array ? response.data : null),
      moduleName: payload && payload.config ? 'config' : undefined,
    })
      .then(({ data }) => {
        if (!(data instanceof Array)) {
          toastr.error(i18n.t('message.CONNECTION_ERROR'));
        }
      });
  },

  /** Insert update an action plan */
  async [actionsTypes.SET_ACTION_PLAN](store, payload) {
    return doAsync(store, {
      config: {
        method: 'post',
        url: '/server/actionPlans/set.php',
        data: { ...payload, actions: true },
      },
      mutationTypes: payload.config ? GET_CONFIG_ACTION_PLANS : mutationsTypes.GET_ACTION_PLANS,
      callback: response => (response.data instanceof Array ? response.data : null),
      moduleName: payload.config ? 'config' : undefined,
    })
      .then(({ data }) => {
        if (!(data instanceof Array)) {
          toastr.error(i18n.t('message.CONNECTION_ERROR'));
        }
      });
  },

  /** Delete an action plan */
  async [actionsTypes.DELETE_ACTION_PLAN](store, payload) {
    return doAsync(store, {
      config: {
        method: 'post',
        url: '/server/actionPlans/delete.php',
        data: { ...payload, actions: true },
      },
      mutationTypes: payload.config ? GET_CONFIG_ACTION_PLANS : mutationsTypes.GET_ACTION_PLANS,
      callback: response => (response.data instanceof Array ? response.data : null),
      moduleName: payload.config ? 'config' : undefined,
    })
      .then(({ data }) => {
        if (!(data instanceof Array)) {
          if (data === -2) {
            toastr.error(i18n.t('message.CANT_EDIT_SUPPORT_WITH_ACTIONS'));
          } else {
            toastr.error(i18n.t('message.CONNECTION_ERROR'));
          }
        }
      });
  },

  /** Fetch ongoing securing actions */
  async [actionsTypes.FETCH_ONGOING_SECURING_ACTIONS](store) {
    return doAsync(store, {
      config: {
        method: 'post',
        url: '/server/actions/get.php',
        data: { ongoing: true, securing: true },
      },
      mutationTypes: mutationsTypes.GET_ONGOING_SECURING_ACTIONS,
      callback: response => (response.data instanceof Array ? response.data : null),
    })
      .then(({ data }) => {
        if (!(data instanceof Array)) {
          toastr.error(i18n.t('message.CONNECTION_ERROR'));
        }
      });
  },

  /** Fetch ongoing quick actions */
  async [actionsTypes.FETCH_ONGOING_QUICK_ACTIONS](store) {
    return doAsync(store, {
      config: {
        method: 'post',
        url: '/server/actions/get.php',
        data: { ongoing: true, quickAction: true },
      },
      mutationTypes: mutationsTypes.GET_ONGOING_QUICK_ACTIONS,
      callback: response => (response.data instanceof Array ? response.data : []),
    })
      .then(({ data }) => {
        if (!(data instanceof Array)) {
          toastr.error(i18n.t('message.CONNECTION_ERROR'));
        }
      });
  },

  async [actionsTypes.FETCH_MANAGED_ACTION_PLANS](store, payload) {
    return doAsync(store, {
      config: {
        method: 'post',
        url: '/server/management/getActionPlans.php',
        data: payload,
      },
      mutationTypes: mutationsTypes.GET_MANAGED_ACTION_PLANS,
      callback: response => (response.data instanceof Array ? response.data : null),
    })
      .then(({ data }) => {
        if (!(data instanceof Array)) {
          toastr.error(i18n.t('message.CONNECTION_ERROR'));
        }
      });
  },

  async [actionsTypes.FETCH_LIBRARY_INDICATORS](store, payload) {
    return doAsync(store, {
      config: {
        method: 'post',
        url: '/server/config/library/get.php',
        data: payload,
      },
      mutationTypes: mutationsTypes.GET_LIBRARY_INDICATORS,
      callback: response => (response.data instanceof Array ? response.data : null),
    })
      .then(({ data }) => {
        if (!(data instanceof Array)) {
          toastr.error(i18n.t('message.CONNECTION_ERROR'));
        }
      });
  },

  /** Fetch the indicator and themes of the current team */
  async [actionsTypes.FETCH_INDICATOR_THEMES](store, payload) {
    return doAsync(store, {
      config: {
        method: 'post',
        url: '/server/indicators/get.php',
        data: payload,
      },
      mutationTypes: mutationsTypes.GET_INDICATOR_THEMES,
      callback: (response) => {
        const data = handleIndicatorsResponse(store, response, payload);
        if (data === null) {
          return null;
        }
        const newDataIds = data.map(theme => theme.id);
        const previousThemes = (store.state[mutationsTypes.GET_INDICATOR_THEMES.stateKey] || [])
          .filter(theme => !newDataIds.includes(theme.id));
        return [...previousThemes, ...data];
      },
    })
      .then(({ data }) => {
        if (!(data instanceof Array)) {
          toastr.error(i18n.t('message.CONNECTION_ERROR'));
          throw data;
        }
        return data;
      });
  },

  /** Fetch indicator schemes data of the teams managed byt the current one */
  async [actionsTypes.FETCH_MANAGED_INDICATORS](store, payload) {
    return doAsync(store, {
      config: {
        method: 'post',
        url: '/server/management/getTeamsIndicators.php',
        data: payload,
      },
      mutationTypes: mutationsTypes.GET_MANAGED_INDICATORS,
      callback: (response) => {
        if (response.data instanceof Object) {
          Object.freeze(response.data);
          return response.data;
        }
        return null;
      },
    })
      .then(({ data }) => {
        if (!(data instanceof Object)) {
          toastr.error(i18n.t('message.CONNECTION_ERROR'));
        }
        // Save indicators data
        // NOTE: Previous note was saying : "Optimize a bit the following code (otherwise,
        // browsr is completely stuck for 20 seconds when saving)"
        // Actually, that was due to GET_MANAGED_INDICATORS, and not the lines below.
        // Freezing the object in GET_MANAGED_INDICATORS fixed the issue
        data.forEach((team) => {
          team.themes.forEach((theme) => {
            (theme.indicators || []).forEach((indicator) => {
              indicator.id = Number(indicator.id);
              store.commit(mutationsTypes.SET_INDICATOR, {
                indicator,
                range: payload.range,
              });
            });
          });
        });
        return data;
      });
  },

  async [actionsTypes.FETCH_INDICATOR](store, payload) {
    return Vue.axios({
      method: 'post',
      url: '/server/indicators/get.php',
      data: payload,
    }).then((response) => {
      const data = handleIndicatorsResponse(store, response, payload);
      if (data && data.length && data[0].indicators) {
        // This is an array of themes
        store.commit(mutationsTypes.SET_TEAM_THEMES, data);
      }
      return data;
    });
  },

  /** Add or edit an IndicatorObjective entry */
  async [actionsTypes.SET_INDICATOR_OBJECTIVE](store, payload) {
    return Vue.axios({
      method: 'post',
      url: '/server/indicators/setObjective.php',
      data: payload,
    }).then(response => handleIndicatorsResponse(store, response, payload));
  },

  /** Add or edit an IndicatorValue entry */
  async [actionsTypes.SET_INDICATOR_VALUE](store, payload) {
    const { cancelToken } = payload;
    delete payload.cancelToken;
    return Vue.axios({
      method: 'post',
      url: '/server/indicators/setValue.php',
      data: payload,
      cancelToken,
    }).then(response => handleIndicatorsResponse(store, response, payload)).catch(error => error);
  },

  /** Delete an IndicatorValue entry */
  async [actionsTypes.DELETE_INDICATOR_VALUE](store, payload) {
    return Vue.axios({
      method: 'post',
      url: '/server/indicators/deleteValue.php',
      data: payload,
    }).then(response => handleIndicatorsResponse(store, response, payload));
  },

  /** Fetch cumulated indicators and their values in a time range */
  async [actionsTypes.FETCH_CUMULATED_INDICATORS](store, payload) {
    return Vue.axios({
      method: 'post',
      url: '/server/management/getIndicators.php',
      data: payload,
    })
      .then(({ data }) => {
        if (data instanceof Object) {
          store.commit(mutationsTypes.SET_CUMULATED_INDICATORS, data);
          (data.indicators || []).forEach((indicator) => {
            indicator.id = Number(indicator.id);
            store.commit(mutationsTypes.SET_INDICATOR, {
              indicator,
              range: payload ? payload.range : undefined,
            });
          });
          return data;
        }
        throw data;
      });
  },

  /** This action doesn't return any data. It only buffer a query to group indicators XHR */
  async [actionsTypes.BUFFER_FETCH_CUMULATED_INDICATORS](store, payload) {
    clearTimeout(pendingMgtIndicatorsRequestTimeout);
    // Find another request with the same range and simply add the indicator id to it
    const sameRange = pendingMgtIndicatorsRequests
      .find(req => req.range.start.isSame(payload.range.start) && req.range.end.isSame(payload.range.end));

    if (sameRange) {
      const arrayParams = ['libraryIds', 'teams'];
      arrayParams.forEach((param) => {
        if (payload[param]) {
          sameRange[param] = uniq([...sameRange[param], ...payload[param]]);
        }
      });
    } else {
      pendingMgtIndicatorsRequests.push(payload);
    }

    pendingMgtIndicatorsRequestTimeout = setTimeout(() => {
      const promises = pendingMgtIndicatorsRequests
        .map(req => store.dispatch(actionsTypes.FETCH_CUMULATED_INDICATORS, { ...req, range: req.range.sStrRange }));
      pendingMgtIndicatorsRequests = [];
      Promise.all(promises);
    }, 300);
  },

  /** This action doesn't return any data. It only buffer a query to group indicators XHR */
  async [actionsTypes.BUFFER_FETCH_INDICATORS](store, payload) {
    clearTimeout(pendingIndicatorsRequestTimeout);
    // Find another request with the same range and simply add the indicator id to it
    const sameRange = pendingIndicatorsRequests
      .find(req => req.range.start.isSame(payload.range.start) && req.range.end.isSame(payload.range.end));
    if (sameRange) {
      sameRange.indicatorId = uniq([...sameRange.indicatorId, ...payload.indicatorId]);
    } else {
      pendingIndicatorsRequests.push(payload);
    }

    pendingIndicatorsRequestTimeout = setTimeout(() => {
      const promises = pendingIndicatorsRequests
        .map(req => store.dispatch(actionsTypes.FETCH_INDICATOR, { ...req, range: req.range.sStrRange }));
      pendingIndicatorsRequests = [];
      Promise.all(promises);
    }, 300);
  },

  /** Fetch indicators display configurations. See IndicatorDisplayConfig */
  async [actionsTypes.FETCH_INDICATOR_DISPLAY_CONFIGS](store, payload) {
    return doAsync(store, {
      config: {
        method: 'post',
        url: '/server/indicators/displayConfigs/get.php',
        data: payload,
      },
      mutationTypes: mutationsTypes.GET_INDICATOR_DISPLAY_CONFIGS,
      callback: response => (response.data instanceof Array ? response.data : null),
    })
      .then(({ data }) => {
        if (!(data instanceof Array)) {
          toastr.error(i18n.t('message.CONNECTION_ERROR'));
        }
      });
  },

  /** Fetch indicators as an array of indicator objects */
  async [actionsTypes.FETCH_INDICATORS_ONLY](store, payload) {
    store.commit(mutationsTypes.SET_INDICATORS_ARRAY_LOADING, { ...payload, loading: true });
    return Vue.axios({
      method: 'post',
      url: '/server/indicators/get.php',
      data: { indicatorsOnly: true, ...payload },
    })
      .then(({ data }) => {
        if (data instanceof Array) {
          store.commit(mutationsTypes.SET_INDICATORS_ARRAY, { ...payload, indicators: data });
        }
        store.commit(mutationsTypes.SET_INDICATORS_ARRAY_LOADING, { ...payload, loading: false });
        return data;
      });
  },

  /** Fetch warnings of indicator(s) */
  async [actionsTypes.FETCH_INDICATOR_WARNINGS](store, payload) {
    return Vue.axios({
      method: 'post',
      url: '/server/indicators/warnings/get.php',
      data: payload,
    })
      .then(({ data }) => {
        if (data instanceof Object) {
          store.commit(mutationsTypes.SET_INDICATOR_WARNINGS, data);
          return data;
        }
        throw data;
      });
  },

  /** Fetch the information for this team */
  async [actionsTypes.FETCH_INFOS](store, payload) {
    store.commit(mutationsTypes.SET_INFO_DATA_LOADING, { ...payload, loading: true });
    // If fetching info with specific conditions, use basic axios function. Otherwise, doAsync
    const config = { method: 'post', url: '/server/information/get.php', data: { includePending: true, ...payload } };
    const responseHandler = ({ data }) => {
      if (!(data instanceof Object)) {
        toastr.error(i18n.t('message.CONNECTION_ERROR'));
        throw data;
      }
      store.commit(mutationsTypes.SET_INFO_DATA, { ...payload, data });
      return data;
    };

    if (payload && (payload.id !== undefined || payload.date !== undefined)) {
      return Vue.axios(config).then(responseHandler);
    }
    return doAsync(store, {
      config,
      mutationTypes: mutationsTypes.GET_INFOS,
      callback: response => (response.data instanceof Object ? response.data : null),
    })
      .then(responseHandler);
  },

  /** Edit an information */
  async [actionsTypes.EDIT_INFO](store, payload) {
    return Vue.axios({
      method: 'put',
      url: '/server/information/edit.php',
      data: payload,
    }).then(({ data }) => {
      if (!(data instanceof Object)) {
        toastr.error(i18n.t('message.CONNECTION_ERROR'));
        throw data;
      }
      store.commit(mutationsTypes.SET_INFO_DATA, { ...payload, data });
      return data;
    });
  },

  /** Delete an information */
  async [actionsTypes.DELETE_INFO](store, payload) {
    return Vue.axios({
      method: 'post',
      url: '/server/information/delete.php',
      data: payload,
    }).then(({ data }) => {
      if (!(data instanceof Object)) {
        toastr.error(i18n.t('message.CONNECTION_ERROR'));
        throw data;
      }
      store.commit(mutationsTypes.SET_INFO_DATA, { ...payload, data });
      return data;
    });
  },

  // Problem types CRUD
  async [actionsTypes.FETCH_PB_TYPES](store, payload) {
    return pbTypeAction(store, payload, 'get');
  },
  async [actionsTypes.UPDATE_PB_TYPE](store, payload) {
    return pbTypeAction(store, payload, 'put');
  },
  async [actionsTypes.CREATE_PB_TYPE](store, payload) {
    return pbTypeAction(store, payload, 'post');
  },
  async [actionsTypes.DELETE_PB_TYPE](store, payload) {
    return pbTypeAction(store, payload, 'delete');
  },

  // Zones CRUD
  // TODO: Use a proper function to automatically create CRUD store actions and action-types
  async [actionsTypes.FETCH_ZONES](store, payload) {
    return zoneAction(store, payload, 'get');
  },
  async [actionsTypes.UPDATE_ZONE](store, payload) {
    return zoneAction(store, payload, 'put');
  },
  async [actionsTypes.CREATE_ZONE](store, payload) {
    return zoneAction(store, payload, 'post');
  },
  async [actionsTypes.DELETE_ZONE](store, payload) {
    return zoneAction(store, payload, 'delete');
  },


  /** Fetch company's keywords */
  async [actionsTypes.FETCH_KEYWORDS](store) {
    return doAsync(store, {
      config: {
        method: 'post',
        url: '/server/keywords/get.php',
        data: {},
      },
      mutationTypes: mutationsTypes.GET_KEYWORDS,
      callback: (response) => {
        if (response.data instanceof Array) {
          return response.data.map(kw => ({
            ...kw,
            label: kw.word,
          }));
        }
        return null;
      },
    })
      .then(({ data }) => {
        if (!(data instanceof Array)) {
          toastr.error(i18n.t('message.CONNECTION_ERROR'));
        }
      });
  },

  /** Fetch screen saver data */
  async [actionsTypes.FETCH_SCREEN_SAVER](store) {
    return doAsync(store, {
      config: {
        method: 'post',
        url: '/server/screenSaver/get.php',
        data: {},
      },
      mutationTypes: mutationsTypes.GET_SCREEN_SAVER,
      callback: response => (response.data instanceof Array ? response.data : null),
    })
      .then(({ data }) => {
        if (!(data instanceof Array)) {
          toastr.error(i18n.t('message.CONNECTION_ERROR'));
        }
      });
  },

  /** Fetch upcoming meetings */
  async [actionsTypes.FETCH_NEXT_MEETINGS](store, payload) {
    return doAsync(store, {
      config: {
        method: 'post',
        url: '/server/meetings/get.php',
        data: { ...payload, next: true, now: Moment().format() },
      },
      mutationTypes: mutationsTypes.GET_NEXT_MEETINGS,
      callback: response => (response.data instanceof Array ? response.data : null),
    })
      .then(({ data }) => {
        if (!(data instanceof Array)) {
          toastr.error(i18n.t('message.CONNECTION_ERROR'));
        }
      });
  },

  /** Fetch stickers */
  async [actionsTypes.FETCH_STICKERS](store, payload) {
    return doAsync(store, {
      config: {
        method: 'post',
        url: '/server/stickers/get.php',
        data: payload,
      },
      mutationTypes: mutationsTypes.GET_STICKERS,
      callback: response => (response.data instanceof Array ? response.data : null),
    })
      .then(({ data }) => {
        if (!(data instanceof Array)) {
          toastr.error(i18n.t('message.CONNECTION_ERROR'));
        }
      });
  },

  /** Edit or add a sticker */
  async [actionsTypes.SET_STICKER](store, payload) {
    return doAsync(store, {
      config: {
        method: 'post',
        url: '/server/stickers/set.php',
        data: payload,
      },
      mutationTypes: mutationsTypes.GET_STICKERS,
      callback: response => (response.data instanceof Array ? response.data : null),
    })
      .then(({ data }) => {
        if (!(data instanceof Array)) {
          toastr.error(i18n.t('message.CONNECTION_ERROR'));
        }
      });
  },

  /** Delete a sticker */
  async [actionsTypes.DELETE_STICKER](store, payload) {
    return doAsync(store, {
      config: {
        method: 'post',
        url: '/server/stickers/delete.php',
        data: payload,
      },
      mutationTypes: mutationsTypes.GET_STICKERS,
      callback: response => (response.data instanceof Array ? response.data : null),
    })
      .then(({ data }) => {
        if (!(data instanceof Array)) {
          toastr.error(i18n.t('message.CONNECTION_ERROR'));
        }
      });
  },

  /** Fetch current announcement */
  async [actionsTypes.FETCH_ANNOUNCEMENTS](store, payload) {
    store.commit(mutationsTypes.INIT_HIDDEN_ANNOUNCEMENTS);
    return doAsync(store, {
      config: {
        method: 'post',
        url: '/server/announcements/get.php',
        data: payload,
      },
      mutationTypes: mutationsTypes.GET_ANNOUNCEMENTS,
      callback: response => (response.data instanceof Array ? response.data : null),
    })
      .then(({ data }) => {
        if (!(data instanceof Array)) {
          toastr.error(i18n.t('message.CONNECTION_ERROR'));
        }
      });
  },

  async [actionsTypes.FETCH_LOGO](store) {
    return doAsync(store, {
      config: {
        method: 'get',
        url: '/server/company/getLogo.php',
      },
      mutationTypes: mutationsTypes.GET_LOGO,
      callback: ({ data }) => {
        if (data instanceof Object) {
          return { url: String(data.logo), whiteBg: data.logoWhiteBg === 1 };
        }
        return null;
      },
    });
  },

  /**
   * Assign an action to a plan
   * payload must be provided as an object containing:
   * - action: the relevant action object
   * - planId: the new plan value
   * - [fromPlanId]: Optional plan from which this action is taken
   * - [supportTeamId]: Optional If sending this action to a support team, id of the team
   */
  async [actionsTypes.ASSIGN_ACTION](store, payload) {
    // Specific case that requires a prompt : Action was assigned from Team A
    // to team B and team B tries to assign it to team A again
    const {
      sameTeamConfirmation,
      action,
      supportTeamId,
      planId,
      fromPlanId,
    } = payload;
    if (
      !sameTeamConfirmation
      && action.fromTeam !== null
      && !isNaN(action.fromTeam)
      && action.fromTeam === supportTeamId
    ) {
      return new Promise((resolve, reject) => {
        calert(i18n.t('message.CONFIRM_ASSIGN_SAME_SUPPORT'), [
          {
            title: i18n.t('message.YES'),
            action: () => {
              // User confirmed => call the same action with the confirmation param
              store.dispatch(actionsTypes.ASSIGN_ACTION, { ...payload, sameTeamConfirmation: true })
                .then((res) => {
                  resolve(res);
                })
                .catch((err) => {
                  reject(err);
                });
            },
          },
          {
            title: i18n.t('message.NO'),
          },
        ]);
      });
    }
    const params = { plan: planId || planId === 0 ? planId : (action.plan || 0), id: action.id };
    if (!isNaN(fromPlanId)) {
      params.fromPlan = fromPlanId;
    }
    if (!isNaN(supportTeamId)) {
      params.supportTeam = supportTeamId;
    }
    return Vue.axios({
      method: 'post',
      url: '/server/actions/assign.php',
      data: params,
    })
      .then(({ data }) => {
        if (typeof data === 'object' && data.id) {
          store.dispatch(`socket/${SEND_MESSAGE}`, SocketMessages.CC_MSGS.PENDING_AND_PLANS);
          if (!isNaN(data.supportTeamId)) {
            store.dispatch(`socket/${SEND_MESSAGE}`, { msg: SocketMessages.CC_MSGS.PENDING, team: data.supportTeamId });
            store.dispatch(`socket/${SEND_MESSAGE}`, { msg: SocketMessages.NOTIF_MSG, team: data.supportTeamId });
          }
          store.dispatch('FETCH_PENDING_ACTIONS_NUMBER', true);
          toastr.success(i18n.t('message.ACTION_ASSIGNED'));
          return data;
        }

        if (data === -1) {
          if (!isNaN(supportTeamId)) {
            // Team param = current team
            calert(i18n.t('message.CANT_ASSIGN_SELF'), [{ title: i18n.t('message.CANCEL') }]);
          } else {
            // Current team doesn't own this plan
            toastr.error(i18n.t('message.ONLY_PLAN_OWNER_ASSIGN'));
          }
        } else if (data === -2) {
          // Plan is full
          calert(i18n.t('message.PLAN_FULL'), [{ title: i18n.t('message.CANCEL') }]);
        } else if (data === -3) {
          // Action is supposedly in another plan and shouldn't be displayed here
          toastr.error(i18n.t('message.ACTION_MAYBE_IN_OTHER_PLAN'));
        }
        throw data;
      })
      .catch((err) => {
        throw err;
      });
  },

  async [actionsTypes.RETURN_ACTION](store, payload) {
    return Vue.axios({
      method: 'post',
      url: '/server/actionPlans/return.php',
      data: { id: payload.id },
    }).then(({ data }) => {
      if (data === 1) {
        toastr.success(i18n.t('message.ACTION_RETURNED'));
        if (payload.team) {
          store.dispatch(`socket/${SEND_MESSAGE}`, { msg: SocketMessages.CC_MSGS.ACTION_EDITION, ...payload });
          store.dispatch(`socket/${SEND_MESSAGE}`, { msg: SocketMessages.CC_MSGS.ACTION_EDITION, id: payload.id });
          store.dispatch(`socket/${SEND_MESSAGE}`, { msg: SocketMessages.CC_MSGS.PENDING, team: payload.team });
          store.dispatch(`socket/${SEND_MESSAGE}`, { msg: SocketMessages.NOTIF_MSG, team: payload.team });
          store.dispatch(`socket/${SEND_MESSAGE}`, SocketMessages.CC_MSGS.PENDING);
          store.dispatch(`socket/${SEND_MESSAGE}`, SocketMessages.CC_MSGS.ACTION_PLANS);
        }
        return data;
      }
      toastr.error(i18n.t('message.CONNECTION_ERROR'));
      throw data;
    }).catch((err) => {
      throw err;
    });
  },

  /** Fetch the postponement reasons of the previous actions */
  async [actionsTypes.FETCH_ACTION_POSTPMT_REASONS](store) {
    return doAsync(store, {
      config: {
        method: 'post',
        url: '/server/actions/getPostponementReasons.php',
      },
      mutationTypes: mutationsTypes.GET_ACTION_POSTPMT_REASONS,
      callback: response => (response.data instanceof Array ? response.data : null),
    });
  },


  // ==================
  // Cfg pages related actions
  async [actionsTypes.FETCH_MENU_CFG_PAGES](store) {
    return doAsync(store, {
      config: {
        method: 'post',
        url: '/server/customPages/get.php',
        data: { inMenu: true },
      },
      mutationTypes: mutationsTypes.GET_MENU_CFG_PAGES,
      callback: response => (response.data instanceof Array ? response.data : null),
    })
      .then(({ data }) => {
        if (!(data instanceof Array)) {
          toastr.error(i18n.t('message.CONNECTION_ERROR'));
        }
      });
  },

  async [actionsTypes.FETCH_CFG_PAGES](store) {
    return doAsync(store, {
      config: {
        method: 'post',
        url: '/server/customPages/get.php',
      },
      mutationTypes: mutationsTypes.GET_CFG_PAGES,
      callback: response => (response.data instanceof Array ? response.data : null),
    })
      .then(({ data }) => {
        if (!(data instanceof Array)) {
          toastr.error(i18n.t('message.CONNECTION_ERROR'));
        }
      });
  },
  /** Fetch a cfg page using its id */
  async [actionsTypes.FETCH_CFG_PAGE](store, payload) {
    Vue.axios({
      method: 'post',
      url: '/server/customPages/get.php',
      data: payload,
    })
      .then(({ data }) => {
        if (data instanceof Object) {
          store.commit(mutationsTypes.SET_CFG_PAGE, data);
        }
      });
  },

  /** Fetch a cfg page */
  async [actionsTypes.SET_CFG_PAGE](store, payload) {
    Vue.axios({
      method: 'post',
      url: '/server/customPages/set.php',
      data: payload,
    })
      .then(({ data }) => {
        if (data instanceof Object) {
          store.commit(mutationsTypes.SET_CFG_PAGE, data);
        }
      });
  },

  /** Create a cfg page */
  async [actionsTypes.CREATE_CFG_PAGE](store, payload) {
    Vue.axios({
      method: 'post',
      url: '/server/customPages/create.php',
      data: payload,
    })
      .then((response) => {
        const { data } = response;
        if (data instanceof Array) {
          toastr.success(i18n.t('message.CFG_PAGES_CREATED'));
          return data;
        }
        if (data === ErrorCode.CFG_PAGE_ALREADY_HAS_TEMPLATE) {
          toastr.error(i18n.t('message.CFG_PAGE_ALREADY_HAS_TEMPLATE'));
        } else {
          toastr.error(i18n.t('message.CONNECTION_ERROR'));
        }
        throw response;
      });
  },

  /** Fetch a cfg page widget */
  async [actionsTypes.FETCH_CFG_PAGE_WIDGET](store, payload) {
    Vue.axios({
      method: 'post',
      url: '/server/customPages/getWidget.php',
      data: payload,
    })
      .then(({ data }) => {
        if (data instanceof Object) {
          store.commit(mutationsTypes.SET_CFG_PAGE_WIDGET, data);
        }
      });
  },

  /** Set a cfg page widget's params */
  async [actionsTypes.SET_CFG_PAGE_WIDGET](store, payload) {
    return Vue.axios({
      method: 'post',
      url: '/server/customPages/setWidget.php',
      data: payload,
    })
      .then(({ data }) => {
        if (data instanceof Object) {
          store.commit(mutationsTypes.SET_CFG_PAGE_WIDGET, data);
          return data;
        }
        if (data === ErrorCode.CFG_PAGE_EDITING_SYNCED_TEMPLATE) {
          toastr.remove();
          toastr.error(i18n.t('message.CFG_PAGE_CANT_EDIT_TEMPLATE_WIDGET'), '', { preventDuplicates: false });
        }
        throw data;
      });
  },

  /** Delete a cfg page widget */
  async [actionsTypes.DELETE_CFG_PAGE_WIDGET](store, payload) {
    Vue.axios({
      method: 'post',
      url: '/server/customPages/deleteWidget.php',
      data: payload,
    })
      .then(({ data }) => {
        if (data instanceof Object) {
          store.commit(mutationsTypes.SET_CFG_PAGE, data);
        } else if (data === ErrorCode.CFG_PAGE_EDITING_SYNCED_TEMPLATE) {
          toastr.remove();
          toastr.error(i18n.t('message.CFG_PAGE_CANT_EDIT_TEMPLATE_WIDGET'), '', { preventDuplicates: false });
        }
      });
  },

  /** Delete a cfg page widget */
  async [actionsTypes.SET_TEXT_PER_DATE_WIDGET](store, payload) {
    const { cancelToken } = payload;
    delete payload.cancelToken;
    return Vue.axios({
      method: 'post',
      url: '/server/customPages/textPerDate/setText.php',
      data: payload,
      cancelToken,
    })
      .then(({ data }) => {
        if (data instanceof Object) {
          store.commit(mutationsTypes.SET_TEXT_PER_DATE_WIDGET, data);
        } else {
          toastr.error(i18n.t('message.CONNECTION_ERROR'));
        }
        return data;
      });
  },

  /** Delete a cfg page widget */
  async [actionsTypes.FETCH_TEXT_PER_DATE_WIDGET](store, payload) {
    return Vue.axios({
      method: 'post',
      url: '/server/customPages/textPerDate/getText.php',
      data: payload,
    })
      .then(({ data }) => {
        if (data instanceof Object) {
          store.commit(mutationsTypes.SET_TEXT_PER_DATE_WIDGET, data);
        } else if (data === -1) {
          toastr.error(i18n.t('message.CONNECTION_ERROR'));
        }
        return data;
      });
  },

  /** Fetch user notification settings */
  async [actionsTypes.FETCH_NOTIFICATION_SETTINGS](store) {
    return doAsync(store, {
      config: {
        method: 'get',
        url: '/server/notifications/settings',
      },
      mutationTypes: mutationsTypes.GET_NOTIFICATION_SETTINGS,
      callback: ({ data }) => (data instanceof Array ? data : []).map(nt => ({
        ...nt,
        notifType: NotificationType.typesPerId[`${nt.id}`] || {},
      })),
    })
      .then(({ data }) => {
        if (!(data instanceof Array)) {
          toastr.error(i18n.t('message.CONNECTION_ERROR'));
        }
      });
  },

  /** Edit user notification settings */
  async [actionsTypes.EDIT_NOTIFICATION_SETTINGS](store, payload) {
    return doAsync(store, {
      config: {
        method: 'post',
        url: '/server/notifications/settings',
        data: payload,
      },
      mutationTypes: mutationsTypes.GET_NOTIFICATION_SETTINGS,
      callback: ({ data }) => (data instanceof Array ? data : []).map(nt => ({
        ...nt,
        notifType: NotificationType.typesPerId[`${nt.id}`] || {},
      })),
    })
      .then(({ data }) => {
        if (!(data instanceof Array)) {
          toastr.error(i18n.t('message.CONNECTION_ERROR'));
        }
      })
      .catch((error) => {
        if (error.response && error.response.status === 401) {
          toastr.error(i18n.t('message.ERROR_UNAUTHORIZE'));
        } else {
          toastr.error(i18n.t('message.CONNECTION_ERROR'));
        }
      });
  },


  // ==================
  // GW related actions

  /** Fetch upcoming GW */
  async [actionsTypes.FETCH_NEXT_GWS](store, payload) {
    return doAsync(store, {
      config: {
        method: 'post',
        url: '/server/gembaWalks/get.php',
        data: { ...payload, next: true, now: Moment().format() },
      },
      mutationTypes: mutationsTypes.GET_NEXT_GWS,
      callback: response => (response.data instanceof Array ? response.data : null),
    })
      .then(({ data }) => {
        if (!(data instanceof Array)) {
          toastr.error(i18n.t('message.CONNECTION_ERROR'));
        }
      });
  },

  /** Fetch GW checklist item templates (items of checklist templates) */
  async [actionsTypes.FETCH_GW_CL_ITEM_TEMPLATES](store, payload) {
    return doAsync(store, {
      config: {
        method: 'post',
        url: '/server/gembaWalks/checkLists/getItemTemplates.php',
        data: payload,
      },
      mutationTypes: mutationsTypes.GET_GW_CL_ITEM_TEMPLATES,
      callback: response => (response.data instanceof Array ? response.data : null),
    })
      .then(({ data }) => {
        if (!(data instanceof Array)) {
          toastr.error(i18n.t('message.CONNECTION_ERROR'));
        }
      });
  },
};
